import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Result,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import {
  CopyOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { canRevealOnboardingOtp } from '../compoinents/OnboardingOtpReveal';

const { Title, Paragraph, Text } = Typography;

const KIND_META = {
  registration: { label: 'Registration phone verification', color: 'blue' },
  enrollee_register: { label: 'Enrollee account sign-up', color: 'geekblue' },
  enrollee_password_reset: { label: 'Enrollee password reset', color: 'purple' },
  password_reset: { label: 'Policy account password reset', color: 'volcano' },
  staff_password_reset: { label: 'Admin portal password reset', color: 'red' },
};

const WINDOWS = [
  { value: 1, label: 'Last hour' },
  { value: 6, label: 'Last 6 hours' },
  { value: 24, label: 'Last 24 hours' },
  { value: 72, label: 'Last 3 days' },
  { value: 168, label: 'Last 7 days' },
];

function parseDate(value) {
  if (!value) return null;
  const d = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateTime(value) {
  const d = parseDate(value);
  return d ? d.toLocaleString() : value ? String(value) : null;
}

function timeLeft(expiresAt) {
  const d = parseDate(expiresAt);
  if (!d) return null;
  const mins = Math.round((d.getTime() - Date.now()) / 60000);
  if (mins <= 0) return null;
  if (mins < 60) return `${mins} min left`;
  const hours = Math.floor(mins / 60);
  return `${hours} h ${mins % 60} min left`;
}

function copyText(code) {
  if (!code) return;
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard
      .writeText(code)
      .then(() => notification.success({ message: 'Copied', placement: 'topLeft', duration: 1.5 }));
  }
}

/**
 * OTP lookup: every pending one-time code the platform has issued, readable
 * by Customer Support when the SMS / email provider is down. Covers
 * registration phone verification, enrollee account sign-up and password
 * reset, policy-account password reset and (for admin.*) staff password
 * reset. Read-only — codes are never resent from here.
 */
const OtpLookupPage = () => {
  const { permissions } = useAuth();
  const allowed = canRevealOnboardingOtp(permissions);

  const [term, setTerm] = useState('');
  const [activeTerm, setActiveTerm] = useState('');
  const [hours, setHours] = useState(24);
  const [showExpired, setShowExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  const load = useCallback(
    async (q, opts = {}) => {
      const includeExpired = opts.showExpired ?? showExpired;
      const windowHours = opts.hours ?? hours;
      setLoading(true);
      setError(null);
      try {
        const resp = await lib.lookupOtpCodes({ q, hours: windowHours, includeExpired });
        const body = resp?.data;
        if (Number(body?.error) === 0) {
          setRows(Array.isArray(body?.data?.codes) ? body.data.codes : []);
          setMeta(body?.data || {});
        } else {
          setRows([]);
          setMeta({});
          setError(body?.message || 'Could not load codes');
        }
      } catch (e) {
        const status = Number(e?.response?.status || 0);
        setRows([]);
        setMeta({});
        if (status !== 404) {
          setError(e?.response?.data?.message || e?.message || 'Could not load codes');
        }
      } finally {
        setLoading(false);
        setLoadedAt(new Date());
      }
    },
    [hours, showExpired],
  );

  useEffect(() => {
    if (allowed) load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  const search = (value) => {
    const q = String(value ?? term).trim();
    setActiveTerm(q);
    load(q);
  };

  const clear = () => {
    setTerm('');
    setActiveTerm('');
    load('');
  };

  const kindFilters = useMemo(
    () =>
      Object.entries(KIND_META)
        .filter(([kind]) => rows.some((r) => r.kind === kind))
        .map(([kind, m]) => ({ text: m.label, value: kind })),
    [rows],
  );

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 190,
      render: (code, r) =>
        code ? (
          <Space size={4} align="center">
            <Text
              code
              style={{
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: 1,
                textDecoration: r.expired ? 'line-through' : 'none',
                opacity: r.expired ? 0.6 : 1,
              }}
            >
              {code}
            </Text>
            <Tooltip title="Copy">
              <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copyText(code)} />
            </Tooltip>
          </Space>
        ) : (
          <Tooltip title="This code was issued before a readable copy was kept. Ask them to request a new code; it will show here.">
            <Tag color="warning">Not recoverable</Tag>
          </Tooltip>
        ),
    },
    {
      title: 'For',
      dataIndex: 'kind',
      filters: kindFilters,
      onFilter: (value, r) => r.kind === value,
      render: (kind, r) => <Tag color={KIND_META[kind]?.color}>{r.kind_label || KIND_META[kind]?.label || kind}</Tag>,
    },
    {
      title: 'Who',
      key: 'who',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.name || <Text type="secondary">Name not on record</Text>}</Text>
          {r.insurance_no ? <Text type="secondary">Insurance no: {r.insurance_no}</Text> : null}
          {r.policy_no ? <Text type="secondary">Policy: {r.policy_no}</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          {r.phone ? <Text>{r.phone}</Text> : null}
          {r.email ? <Text type="secondary">{r.email}</Text> : null}
          {!r.phone && !r.email ? <Text type="secondary">—</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Issued',
      dataIndex: 'issued_at',
      width: 180,
      render: (v) => formatDateTime(v) || '—',
    },
    {
      title: 'Expires',
      dataIndex: 'expires_at',
      width: 200,
      render: (v, r) => {
        if (!v) return <Text type="secondary">Until used</Text>;
        if (r.expired) return <Tag color="red">Expired {formatDateTime(v)}</Tag>;
        return (
          <Space direction="vertical" size={0}>
            <Tag color="green">{timeLeft(v) || 'Expiring'}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatDateTime(v)}
            </Text>
          </Space>
        );
      },
    },
  ];

  if (!allowed) {
    return (
      <Result
        status="403"
        title="Not available"
        subTitle="Looking up one-time codes requires the onboarding.otp_reveal permission."
      />
    );
  }

  const listing = activeTerm === '';

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
      <Title level={3} style={{ marginTop: 0 }}>
        <SafetyCertificateOutlined /> OTP lookup
      </Title>
      <Paragraph type="secondary">
        Every pending one-time code the system has issued, for when SMS or email is not getting
        through: registration phone verification, enrollee account sign-up and password reset,
        policy account password reset{meta.staff_codes_included ? ', and admin portal password reset' : ''}.
        Read the code to the person after confirming who they are. Nothing is resent from here, and
        every lookup is recorded in the activity log.
      </Paragraph>

      <Card>
        <Space wrap style={{ width: '100%', marginBottom: 12 }}>
          <Input.Search
            placeholder="Phone, insurance no, policy no, email or name"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onSearch={search}
            enterButton={
              <>
                <SearchOutlined /> Search
              </>
            }
            allowClear
            style={{ width: 420 }}
            maxLength={200}
          />
          {listing ? (
            <Select
              value={hours}
              options={WINDOWS}
              style={{ width: 150 }}
              onChange={(v) => {
                setHours(v);
                load('', { hours: v });
              }}
            />
          ) : (
            <Button onClick={clear}>Show all pending</Button>
          )}
          <Space size={6}>
            <Switch
              checked={showExpired}
              onChange={(v) => {
                setShowExpired(v);
                load(activeTerm, { showExpired: v });
              }}
            />
            <Text>Show expired</Text>
          </Space>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(activeTerm)}>
            Refresh
          </Button>
        </Space>

        {error ? <Alert type="error" showIcon message="Lookup failed" description={error} style={{ marginBottom: 12 }} /> : null}

        <Table
          rowKey={(r) => `${r.source}-${r.source_id}`}
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }}
          locale={{
            emptyText: listing
              ? `No pending codes were issued in this window${showExpired ? '' : ' that are still valid'}.`
              : `No pending code matches “${activeTerm}”. They may have already verified, or need to request a new one.`,
          }}
          title={() => (
            <Space split={<Text type="secondary">·</Text>} wrap>
              <Text strong>
                {rows.length} {rows.length === 1 ? 'code' : 'codes'}
              </Text>
              <Text type="secondary">
                {listing ? WINDOWS.find((w) => w.value === hours)?.label : `Search: ${activeTerm}`}
              </Text>
              {loadedAt ? <Text type="secondary">Updated {loadedAt.toLocaleTimeString()}</Text> : null}
            </Space>
          )}
        />
      </Card>
    </div>
  );
};

export default OtpLookupPage;
